import { Injectable, signal, computed } from '@angular/core';
import { DotNotationKeys, LoginResponse, PathValue } from './user';
import { StorageService } from '@core/storage-module/storage.service';

/* -------------------------------------------------------------------------- */
/*                               USER SERVICE                                 */
/* -------------------------------------------------------------------------- */

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly STORAGE_KEY = 'relipay_usr_session';
  private readonly _loginData = signal<LoginResponse | null>(null);
  readonly loginData = computed(() => this._loginData());

  constructor(private readonly storage: StorageService) {
    const saved = this.storage.getItem(this.STORAGE_KEY);
    if (saved) this._loginData.set(saved);
  }

  setLoginData(data: LoginResponse): void {
    this._loginData.set(data);
    this.storage.setItem(this.STORAGE_KEY, data);
  }

  resetLoginData(): void {
    this._loginData.set(null);
    this.storage.removeItem(this.STORAGE_KEY);
  }

  /* --------------------------- GET NESTED VALUE --------------------------- */
  getPartialData<P extends DotNotationKeys<LoginResponse>>(path?: P): PathValue<LoginResponse, P> | LoginResponse | null {
    const data = this._loginData();
    if (!data) return null;
    if (!path) return data;

    const parts = path
      .replace(/\[(\d+)\]/g, '.$1') // convert [0] → .0
      .split('.')
      .filter(Boolean);

    return parts.reduce<any>((obj, key) => {
      if (obj == null) return undefined;
      const isIndex = /^\d+$/.test(key);
      return isIndex ? obj[Number(key)] : obj[key];
    }, data);
  }

  /* --------------------------- SET NESTED VALUE --------------------------- */
  setPartialData<P extends DotNotationKeys<LoginResponse>>(path: P, value: PathValue<LoginResponse, P>): void {
    const current = structuredClone(this._loginData()); // clone to avoid direct mutation
    if (!current) return;

    const parts = path
      .replace(/\[(\d+)\]/g, '.$1') // convert [0] → .0
      .split('.')
      .filter(Boolean);

    let target: any = current;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      const isIndex = /^\d+$/.test(key);

      if (isIndex) {
        const idx = Number(key);
        target[idx] ??= {};
        target = target[idx];
      } else {
        target[key] ??= {};
        target = target[key];
      }
    }

    const finalKey = parts[parts.length - 1];
    const isIndex = /^\d+$/.test(finalKey);
    if (isIndex) {
      target[Number(finalKey)] = value;
    } else {
      target[finalKey] = value;
    }

    this._loginData.set(current);
    this.storage.setItem(this.STORAGE_KEY, current);
  }

  /* ----------------------------- UTIL HELPERS ----------------------------- */
  isLoggedIn(): boolean {
    return !!this._loginData();
  }
}
